import { ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { lckClient } from '../services/lck-api/client'
import { useStoreApp } from './app'
import { useStoreData } from './data'
import type { LckUser } from '../services/lck-api/definitions'

export interface AuthDTO {
  email: string
  password: string
}

export const useStoreAuth = defineStore('auth', () => {
  const authState = ref({
    loading: false,
    error: null as null | Error,
    expiredToken: false,
    isAuthenticated: false,
    user: null as null | LckUser,
    groups: null
  })

  async function authenticate(data: AuthDTO) {
    const { feedDataStore } = useStoreData()

    authState.value.loading = true
    authState.value.error = null
    authState.value.expiredToken = false
    try {
      console.info('[auth store] authenticating user...')
      const result = await lckClient.authenticate({
        strategy: 'local',
        email: data.email,
        password: data.password
      })
      // if (result.user.rules) lckAbilities.update(result.user.rules)
      authState.value.isAuthenticated = true
      authState.value.user = result.user
      await feedDataStore(result.user?.id)
    } catch (error) {
      authState.value.error = error as Error
      authState.value.isAuthenticated = false
    }
    authState.value.loading = false
  }

  async function reAuthenticate() {
    const { appState } = useStoreApp()
    const { feedDataStore } = useStoreData()

    authState.value.loading = true
    authState.value.error = null
    console.info('[auth store] Re authenticating user...')
    // if we aren't online, check if we have a local user stored
    if (!appState.isOnline && localStorage.getItem(LCK_SETTINGS.LOCALSTORAGE_KEY_AUTH_USER)) {
      console.info('[auth store] ...from the localStorage...')
      authState.value.user = JSON.parse(
        localStorage.getItem(LCK_SETTINGS.LOCALSTORAGE_KEY_AUTH_USER) as string
      )
      authState.value.isAuthenticated = true
      feedDataStore(authState.value.user?.id!)
    } else if (localStorage.getItem(LCK_SETTINGS.LOCALSTORAGE_KEY_AUTH_TOKEN)) {
      console.info('[auth store] ...against the LocoKit API...')
      try {
        const result = await lckClient.reAuthenticate()
        if (result.user) {
          authState.value.user = result.user
          authState.value.isAuthenticated = true
          await feedDataStore(result.user?.id)
        }
      } catch (e) {
        authState.value.error = e as Error
        authState.value.isAuthenticated = false
      }
    }
    console.info('[auth store] over. Authenticated : ', authState.value.isAuthenticated)
    authState.value.loading = false
  }

  async function logout() {
    const { resetDataStore } = useStoreData()
    authState.value.isAuthenticated = false
    authState.value.user = null
    await lckClient.logout()
    await resetDataStore()
  }

  /**
   * Persist user data in localStorage
   * every time the data evolve
   */
  watch(
    () => authState.value.user,
    (newUser) => {
      localStorage.setItem(LCK_SETTINGS.LOCALSTORAGE_KEY_AUTH_USER, JSON.stringify(newUser))
    }
  )

  return { authState, authenticate, reAuthenticate, logout }
})
